import { some } from 'lodash'
import React, { memo, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Tooltip } from 'react-tooltip'

const DeviceConnectionStabilityInfo = () => (
  <div>
    The Connection stability score metric represents an estimated quality of a participant’s media connection to the conference.<br/>
    it is essentially a synthetic score (typically from 0 to 100) reflecting: 0 is completely unusable/unstable and <br />
    100 is excellent.
    It's not just a raw network metric, but a combined estimate based on media delivery metrics including:
    <ul>
      <li>RTT (Round-Trip Time)</li>
      <li>Packet loss (upload & download)</li>
      <li>Video/audio bitrate compared to what’s expected</li>
      <li>Bandwidth estimation vs available bandwidth</li>
      <li>Video Bridge feedback (for server-side connections)</li>
    </ul>
  </div>
)

const State = ({ data }) => {
  const [visible, setVisible] = useState(false)
  const stateData = useSelector(state => state.state?.[data.device])
  const state = useMemo(() => stateData || {}, [stateData])

  const downloadBandWidth = useMemo(() => {
    return state?.bandwidth?.download || 0
  }, [state])

  const uploadBandWidth = useMemo(() => {
    return state?.bandwidth?.upload || 0
  }, [state])

  const resolution = useMemo(() => {
    return state?.resolution?.[Object.keys(state?.resolution)[0]] || {height: 0, width: 0}
  }, [state])

  const framerate = useMemo(() => {
    return state?.framerate?.[Object.keys(state?.framerate)[0]] || 0
  }, [state])

  const codec = useMemo(() => {
    return {
      video: state?.codec?.[Object.keys(state?.codec)[0]]?.video || 'NA',
      audio: state?.codec?.[Object.keys(state?.codec)[0]]?.audio || 'NA',
    }
  }, [state])

  const name = useMemo(() => {
    return state.deviceName || data.label
  }, [data, state])

  const sleeping = useMemo(() => {
    return state?.sleeping || false
  }, [state])

  const p2p = useMemo(() => {
    return some(state.transport, t => t.p2p)
  }, [data, state])

  return <div className="d-flex device-state">
    <div className="card">
      <div className="card-body">
        <div className="device-header" onClick={() => setVisible(!visible)}>
          <div className="d-flex">
            <p className="m-0 d-inline-block">{name}</p>
            {
              sleeping ? (
                <svg className="opacity-50" style={{ marginTop: -8 }} width="20px" height="20px" viewBox="0 0 192 192" fill="none">
                  <g stroke="#000000" strokeLinecap="round" strokeLinejoin="round" strokeWidth="12" clipPath="url(#a)">
                  <path d="M30 22h62L30 96h62m25-24h46l-46 55h46m-109 0h36l-36 43h36"/>
                  </g>
                  <defs>
                  <clipPath id="a">
                  <path fill="#ffffff" d="M0 0h192v192H0z"/>
                  </clipPath>
                  </defs>
                </svg>
              ) : null
            }
          </div>
          <div className="ml-3">
            <i className={`fa ${visible ? 'fa-chevron-up': 'fa-chevron-down'}`}></i>
          </div>
        </div>
        <div style={{ display: visible ? 'block' : 'none' }}>
          <hr className="my-2"/>
          <div className="d-flex flex-wrap">
            <div className="mb-2 mr-4">
              <small>Group</small><br />
              {state?.groupName || '-'}
            </div>
            <div className="mb-2 mr-4">
              <small>Device</small><br />
              { state?.info?.os || 'NA' }
            </div>
            <div className="mb-2">
              <small>Browser</small><br />
              { state?.info?.browser || 'NA' }
            </div>
          </div>
          <div className="d-flex">
            <div className="mb-2 mr-4">
              <small>
                Connection Stability
                <i
                  data-tooltip-id={`${state.device}-connection-info`}
                  className="fa fa-info-circle opacity-50 ml-2">
                </i>
              </small><br />
              {state?.connectionQuality}
              <Tooltip
                id={`${state.device}-connection-info`}
                place="top"
                children={<DeviceConnectionStabilityInfo />}
              />
            </div>
            <div className="mb-2">
              <small>Connected</small><br/>
              { p2p ? 'P2P' : 'Video Bridge' }
            </div>
          </div>
          <div className="mb-2">
            <small>Video Bridge RTT</small><br />
            {state?.jvbRTT || 0}ms
          </div>

          <div className="mb-2">
            <small>Server Region</small><br />
            {state?.serverRegion || 'Unknown'}
          </div>

          <div className="mb-2">
            <small>Bandwidth</small><br />
            <i className={`fa fa-arrow-down ${downloadBandWidth < 2500 ? 'text-danger': 'text-success'}`}></i> {downloadBandWidth}kbps | <i className={`fa fa-arrow-up ${uploadBandWidth < 2500 ? 'text-danger' : 'text-success'}`}></i> {uploadBandWidth}kbps
          </div>

          <div className="mb-2">
            <small>Framerate</small><br />
            {framerate} fps
          </div>

          <div className="mb-2">
            <small>Resolution</small><br />
            {resolution.width}x{resolution.height} <br />
            <small><i className="fa fa-video text-success indicator"></i> {codec.video} | <i className="fa fa-microphone text-primary indicator"></i> {codec.audio} | Max {state?.maxEnabledResolution || 0}</small>
          </div>

          <div className="mb-2">
            <small>Packet Loss</small><br />
            <i className="fa fa-arrow-down opacity-25"></i> {state?.packetLoss?.download || 0} | <i className="fa fa-arrow-up opacity-25"></i> {state?.packetLoss?.upload || 0} | Total: {state?.packetLoss?.total || 0}
          </div>
        </div>
      </div>
    </div>
  </div>
}

export const DeviceState = memo(State, (prevProps, nextProps) => prevProps.device === nextProps.device)
